import { useState } from "react";
import client from "@/lib/api";
import usePageTitle from "@/hooks/usePageTitle";
import { CONTACT } from "@/constants/testIds";

const TYPES = [
  { v: "general", label: "General enquiry" },
  { v: "media", label: "Media enquiry" },
  { v: "speaking", label: "Speaking invitation" },
  { v: "investment", label: "Company / investment" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", org: "", type: "general", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  usePageTitle("Contact", "Media enquiries, speaking invitations, partnership requests and general correspondence with the office of Shri Nitin Narang.");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) { setError("Please complete name, email and message."); return; }
    setSending(true);
    try {
      await client.post("/leads", form);
      setDone(true);
      setForm({ name: "", email: "", phone: "", org: "", type: "general", subject: "", message: "" });
    } catch {
      setError("We could not send your message. Please try email instead.");
    } finally { setSending(false); }
  };

  return (
    <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-4">Contact</div>
          <h1 className="serif-title text-5xl lg:text-6xl text-[#0a1628] mb-6">Write to the office.</h1>
          <p className="text-[#4a4a4a] text-[17px] leading-relaxed mb-10">
            For media enquiries, speaking invitations, partnership requests or general correspondence, please use the form. The team replies within 3–5 working days.
          </p>

          <div className="space-y-4 mb-10">
            <a href="mailto:nitin@theideazfactory.com" className="flex items-center gap-4 border hairline p-4 hover:border-[#0a1628] transition-colors">
              <i className="fa-solid fa-envelope text-[#b8975d] w-5"></i>
              <div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#6b6b6b]">Email</div>
                <div className="text-[14px] text-[#0a1628]">nitin@theideazfactory.com</div>
              </div>
            </a>
            <a href="https://wa.me/919988884393" target="_blank" rel="noreferrer" className="flex items-center gap-4 border hairline p-4 hover:border-[#0a1628] transition-colors">
              <i className="fa-brands fa-whatsapp text-[#b8975d] w-5"></i>
              <div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#6b6b6b]">WhatsApp</div>
                <div className="text-[14px] text-[#0a1628]">+91 99888 84393</div>
              </div>
            </a>
          </div>

          <div className="flex items-center gap-3">
            {[
              {h:"https://www.linkedin.com/in/nitinn/", i:"fa-brands fa-linkedin-in"},
              {h:"https://x.com/narangnitin", i:"fa-brands fa-x-twitter"},
              {h:"https://www.instagram.com/withnitin/", i:"fa-brands fa-instagram"},
            ].map((s, i) => (
              <a key={i} href={s.h} target="_blank" rel="noreferrer" className="w-10 h-10 border hairline flex items-center justify-center text-[#0a1628] hover:bg-[#0a1628] hover:text-[#faf8f3] transition-colors">
                <i className={s.i}></i>
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} data-testid={CONTACT.form} className="border hairline bg-white p-7 lg:p-10 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="label-sm">Your name *</label>
                <input data-testid={CONTACT.name} className="form-input" value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label className="label-sm">Email *</label>
                <input data-testid={CONTACT.email} type="email" className="form-input" value={form.email} onChange={set("email")} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="label-sm">Phone</label>
                <input data-testid={CONTACT.phone} className="form-input" value={form.phone} onChange={set("phone")} />
              </div>
              <div>
                <label className="label-sm">Organisation</label>
                <input data-testid={CONTACT.org} className="form-input" value={form.org} onChange={set("org")} />
              </div>
            </div>
            <div>
              <label className="label-sm">Type of enquiry</label>
              <select data-testid={CONTACT.type} className="form-input" value={form.type} onChange={set("type")}>
                {TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-sm">Subject</label>
              <input data-testid={CONTACT.subject} className="form-input" value={form.subject} onChange={set("subject")} />
            </div>
            <div>
              <label className="label-sm">Message *</label>
              <textarea data-testid={CONTACT.message} rows="6" className="form-input" value={form.message} onChange={set("message")} />
            </div>

            {error && <div className="text-[13px] text-red-700 border border-red-300 bg-red-50 p-3">{error}</div>}

            <div className="flex items-center justify-between gap-4 pt-2">
              <p className="text-[12px] text-[#6b6b6b]">By submitting, you consent to the office contacting you in reply.</p>
              <button type="submit" disabled={sending} data-testid={CONTACT.submit} className="btn">
                {sending ? "Sending…" : "Send Message"} <i className="fa-solid fa-arrow-right"></i>
              </button>
            </div>

            {done && (
              <div data-testid={CONTACT.success} className="border border-[#b8975d] bg-[#faf3e2] text-[#0a1628] p-5 flex items-start gap-3">
                <i className="fa-solid fa-circle-check text-[#b8975d] mt-1"></i>
                <div>
                  <div className="font-display text-lg">Thank you.</div>
                  <p className="text-[14px]">Your message has been received by the office. We will be in touch shortly.</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </main>
  );
}
